'use client'

import { Button, Card, Cell, InputText, InputTextArea, Text } from '@vinted/web-ui'
import { ReactNode, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { CountryCode } from '@marketplace-web/domain/country-data'
import { navigateToPage, useLocation } from '@marketplace-web/shared/browser'
import { isEmailValid } from '@marketplace-web/shared/email-validation'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session'
import { useSystemConfiguration } from '@marketplace-web/shared/system-configuration'
import { renderValidation } from '@marketplace-web/vendor-abstractions/react-hook-form-ui'
import { UiState } from '@marketplace-web/shared/ui-state'
import { CONVERSATION_URL } from '@marketplace-web/messaging/conversation-messaging-data'

import {
  HELP_CENTER_URL,
  HELP_FAQ_ENTRIES_URL,
  PRIVACY_POLICY_URL,
  TERMS_URL,
} from 'constants/routes'
import { createSupportTicket } from 'data/api/create-support-ticket'
import useHelpCenterSessionTracking from 'hooks/useHelpCenterSessionTracking'

import { Field, FormModel } from './types'

export const TRENDSALES_FAQ_ENTRY_ID = 1317

const TrendsalesPayoutForm = () => {
  const translate = useTranslate('trendsales_form')
  const legalNoteTranslation = useTranslate('complaint_escalation_form')

  const [uiState, setUiState] = useState(UiState.Idle)
  const { searchParams } = useLocation()
  const { faq_entry_id, ch } = searchParams
  const channel = typeof ch === 'string' ? ch : 'help_center'
  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  const isTrendSalesFormEnabled = useFeatureSwitch('trendsales_form')
  const currentUserId = useSession().user?.id

  const { userCountry: countryCode } = useSystemConfiguration() || {}

  useEffect(() => {
    const allowedCountries = [CountryCode.Dk, CountryCode.Pl, CountryCode.Se, CountryCode.Fi]

    if (
      !isTrendSalesFormEnabled ||
      (!countryCode && !allowedCountries.includes(countryCode as CountryCode))
    ) {
      navigateToPage(HELP_CENTER_URL)
    }
  }, [countryCode, isTrendSalesFormEnabled])

  const renderLegalNote = () => {
    const values = {
      'terms-link': (chunks: Array<ReactNode>) => (
        <a key="terms-link" href={TERMS_URL} target="_blank" rel="noreferrer">
          {chunks}
        </a>
      ),
      'privacy-link': (chunks: Array<ReactNode>) => (
        <a key="privacy-link" href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
          {chunks}
        </a>
      ),
    }

    return (
      <div role="link" tabIndex={0}>
        <Text
          as="h4"
          type={Text.Type.Caption}
          text={legalNoteTranslation('legal_footer_confirmation', values)}
        />
      </div>
    )
  }

  function isIbanValid(value) {
    const minLength = 15
    const maxLength = 35
    const trimmedValue = value.trim()

    if (!trimmedValue) return true

    if (trimmedValue.length < minLength) {
      return translate('iban.validation.invalid_too_short')
    }
    if (trimmedValue.length > maxLength) {
      return translate('iban.validation.invalid_too_long')
    }

    const ibanPattern =
      /^\s*(AT|BE|BG|CH|CY|CZ|DE|DK|EE|ES|FI|FR|GB|GI|GR|HR|HU|IE|IS|IT|LI|LT|LU|LV|MC|MT|NL|NO|PL|PT|RO|SE|SI|SK)\s*[A-Z\d]+\s*$/i

    if (!ibanPattern.test(trimmedValue)) {
      return translate('iban.validation.invalid')
    }

    return true
  }

  const formMethods = useForm<FormModel>({ shouldUnregister: false })
  const {
    register,
    handleSubmit,
    getValues,
    formState: { isSubmitting, errors },
  } = formMethods

  async function handleCreateTicket() {
    const formValues = getValues()
    const formattedMessage = `
      Trendsales payout
      Unique code: ${formValues.uniqueCode}
      Full name: ${formValues.fullName}
      email: ${formValues.email}
      IBAN: ${formValues.iban}
      comment: ${formValues.otherInformation}
    `

    setUiState(UiState.Pending)

    const response = await createSupportTicket({
      faqEntryId: Number(faq_entry_id) || TRENDSALES_FAQ_ENTRY_ID,
      ch: channel,
      message: formattedMessage,
      helpCenterSessionId,
      email: formValues.email,
    })

    setUiState(UiState.Success)

    if ('errors' in response) {
      return
    }

    if (currentUserId && response.user_msg_thread_id) {
      navigateToPage(CONVERSATION_URL(response.user_msg_thread_id))
    } else {
      navigateToPage(HELP_FAQ_ENTRIES_URL)
    }
  }

  return (
    <div className="page-container">
      <Cell
        title={
          <Text as="h1" text={translate('title')} theme="amplified" type={Text.Type.Heading} />
        }
      />
      <Cell>
        <Text as="span" text={translate('subtitle')} type={Text.Type.Body} />
      </Cell>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(handleCreateTicket)}>
          <Cell>
            <Card>
              <InputText
                {...register(Field.Email, {
                  required: { value: true, message: translate('email.validation.empty') },
                  validate: {
                    format: value => isEmailValid(value) || translate('email.validation.invalid'),
                  },
                })}
                title={translate('email.title')}
                placeholder={translate('email.placeholder')}
                styling={InputText.Styling.Wide}
                validation={renderValidation(errors[Field.Email]?.message || null)}
                testId="email-field"
              />
              <InputText
                {...register(Field.UniqueCode)}
                title={translate('unique_code.title')}
                placeholder={translate('unique_code.placeholder')}
                styling={InputText.Styling.Wide}
                testId="unique-code-field"
              />
              <InputText
                {...register(Field.FullName)}
                title={translate('full_name.title')}
                placeholder={translate('full_name.placeholder')}
                styling={InputText.Styling.Wide}
                testId="full-name-field"
              />
              <InputText
                {...register(Field.Iban, {
                  validate: isIbanValid,
                })}
                title={translate('iban.title')}
                placeholder={translate('iban.placeholder')}
                styling={InputText.Styling.Wide}
                validation={renderValidation(errors[Field.Iban]?.message || null)}
                testId="iban-field"
              />
              <InputText
                {...register(Field.Swift)}
                title={translate('swift.title')}
                placeholder={translate('swift.placeholder')}
                styling={InputText.Styling.Wide}
                testId="swift-field"
              />
              <InputTextArea
                {...register(Field.OtherInformation)}
                title={translate('other_information.title')}
                placeholder={translate('other_information.placeholder')}
                styling={InputText.Styling.Wide}
                testId="other-information-field"
              />
            </Card>
          </Cell>
          <Cell
            styling={Cell.Styling.Wide}
            suffix={
              <Button
                text={translate('submit')}
                disabled={isSubmitting || uiState === UiState.Success}
                isLoading={isSubmitting}
                styling={Button.Styling.Filled}
                size={Button.Size.Medium}
                type={Button.Type.Submit}
                onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
                  if (isSubmitting) {
                    event.preventDefault()
                  }
                }}
              />
            }
          >
            {renderLegalNote()}
          </Cell>
        </form>
      </FormProvider>
    </div>
  )
}

export default TrendsalesPayoutForm
